import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Button — Apple-style: rounded-2xl, lift on hover, subtle press, layered shadow.
 */
const buttonVariants = cva(
  [
    "group inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "rounded-2xl text-sm font-medium tracking-tight",
    "transition-all duration-200 ease-spring",
    "select-none",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    "[&_svg]:transition-transform hover:[&_svg]:scale-110",
    "hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-primary-600 text-primary-foreground",
          "shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-4px_rgba(28,151,200,0.45)]",
          "hover:bg-primary-700 hover:shadow-[0_2px_4px_rgba(15,23,42,0.08),0_12px_28px_-8px_rgba(28,151,200,0.55)]",
          "active:shadow-[0_1px_2px_rgba(15,23,42,0.08)]",
        ],
        destructive: [
          "bg-destructive text-destructive-foreground",
          "shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-4px_rgba(239,68,68,0.45)]",
          "hover:bg-destructive-600 hover:shadow-[0_12px_28px_-8px_rgba(239,68,68,0.55)]",
        ],
        success: [
          "bg-success text-success-foreground",
          "shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-4px_rgba(16,185,129,0.45)]",
          "hover:bg-success-600 hover:shadow-[0_12px_28px_-8px_rgba(16,185,129,0.55)]",
        ],
        outline: [
          "border border-slate-200/80 bg-white text-slate-700",
          "shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
          "hover:bg-slate-50 hover:border-slate-300 hover:shadow-[0_4px_12px_-4px_rgba(15,23,42,0.12)]",
          "dark:bg-slate-900 dark:border-white/10 dark:text-slate-100 dark:hover:bg-slate-800",
        ],
        secondary: [
          "bg-slate-100 text-slate-900",
          "hover:bg-slate-200",
          "dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700",
        ],
        ghost: [
          "text-foreground",
          "hover:bg-slate-100 hover:-translate-y-0",
          "dark:hover:bg-slate-800",
        ],
        link: [
          "text-primary-700 underline-offset-4 hover:underline hover:-translate-y-0",
          "dark:text-primary-400",
        ],
        whatsapp: [
          "bg-whatsapp text-white",
          "shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-4px_rgba(37,211,102,0.45)]",
          "hover:bg-whatsapp/90 hover:shadow-[0_12px_28px_-8px_rgba(37,211,102,0.55)]",
        ],
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 px-3 text-xs rounded-xl",
        lg: "h-12 px-6 text-base rounded-2xl",
        xl: "h-14 px-8 text-base rounded-2xl",
        icon: "h-10 w-10 rounded-xl",
        "icon-sm": "h-8 w-8 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
